import * as React from 'react'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'
import {
  faEuroSign,
  faFileAlt,
  faInfo,
  faPaperclip
} from '@fortawesome/pro-light-svg-icons'

import { Column, Columns } from '@espoon-voltti/react-components/elements'

import { Section } from '@espoon-voltti/react-components/layout'
import { Collapsible } from '@espoon-voltti/react-voltti/components/collapsible'
import { AttachmentViewer } from '@espoon-voltti/react-voltti/components/attachment-selector'
import { UploadedAttachment } from '@espoon-voltti/react-voltti/components/attachment-selector/domain/model'

import { ProjectGrantFormData } from '@espoon-voltti/nuora-domain/grants'
import { OrgApplicant } from '@espoon-voltti/nuora-domain/applicants'
import { Report } from '../../../domain'
import { t } from '../../../i18n'
import {
  attachmentApiUrl,
  attachmentAuthProfile
} from '../../../services/attachment-service'

import {
  AppDetails,
  ExpensesAndIncomes,
  NuoraOrgInfo,
  OrgInfo
} from '../partials'

interface AppPreviewProps {
  form: ProjectGrantFormData
  applicant: OrgApplicant
  preReport: O.Option<Report>
}

export const ProjectGrantAppPreview: React.FC<AppPreviewProps> = (p) => {
  return (
    <>
      <Section>
        <Collapsible
          dataQa={'orgInfo'}
          title={t(['org', 'basicInfo', 'label'])}
          icon={faInfo}
          open={true}
        >
          <OrgInfo org={p.applicant.org} />
          <NuoraOrgInfo nuoraOrg={p.applicant.nuoraOrg} />
        </Collapsible>
      </Section>
      <Section>
        <Collapsible
          dataQa={'applicationDetails'}
          title={t(['views', 'form', 'appDetails', 'title'])}
          icon={faFileAlt}
          open={true}
        >
          <AppDetails form={p.form} />
        </Collapsible>
      </Section>

      <Section>
        <Collapsible
          dataQa={'expensesAndIncomes'}
          title={t(['views', 'preview', 'expensesAndIncomes', 'title'])}
          icon={faEuroSign}
          open={true}
        >
          <ExpensesAndIncomes
            expenses={p.form.expenses}
            incomes={p.form.incomes}
          />
        </Collapsible>
      </Section>

      <Section dataQa="attachments">
        <Collapsible
          icon={faPaperclip}
          title={t(['views', 'preview', 'attachments', 'app', 'title'])}
        >
          <Columns>
            <Column desktopSize={6} tabletSize={12}>
              <AttachmentViewer
                attachmentApiUrl={attachmentApiUrl}
                authProfile={attachmentAuthProfile}
                models={p.form.attachments}
              />
            </Column>
          </Columns>
        </Collapsible>
      </Section>

      {pipe(
        p.preReport,
        O.map((x) => (
          <Section dataQa="reportAttachments" key="reportAttachments">
            <Collapsible
              icon={faPaperclip}
              title={t(['views', 'preview', 'attachments', 'report', 'title'])}
            >
              <Columns>
                <Column desktopSize={6} tabletSize={12}>
                  <AttachmentViewer
                    attachmentApiUrl={attachmentApiUrl}
                    authProfile={attachmentAuthProfile}
                    models={x.attachments.map(UploadedAttachment.of)}
                  />
                </Column>
              </Columns>
            </Collapsible>
          </Section>
        )),
        O.toNullable
      )}
    </>
  )
}
